import { type FC, memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { isNil, noop } from 'lodash';
// Import Types
import type { VisualizationIcon } from 'types';
// Import Components
import OptionList from 'components/common/OptionList';
import CustomerJourneyVisualizationIcon from './CustomerJourneyVisualizationIcon';
// Import Helpers
import { isCJVisualizationIconAllowed } from 'services/customerJourney/helpers';
// Import Constants
import { l } from 'constants/common';
// Import Enums
import { LabelAppearanceEnum } from 'enums';
// Import Validations
import { yupSchemaValidate } from 'validators/yupUtils';
import { CJVisualizationStepIconValidationSchema } from 'validators/schemas.validation';
// Import Styles
import styles from './customerJourneyVisualizationIconPicker.module.scss';

interface IconListItem {
    value: string;
    icon: JSX.Element;
    name: JSX.Element;
}

interface Props {
    icons?: VisualizationIcon[];
    value?: Nilable<string>;
    onChange?: (value: Nullable<string>) => void;
    isRequired?: boolean;
}

const iconsListCache = new WeakMap<VisualizationIcon[], IconListItem[]>();

const getIconsList = (icons: Nilable<VisualizationIcon[]>): IconListItem[] => {
    if (isNil(icons)) {
        return [];
    }
    const cachedList = iconsListCache.get(icons);
    if (!isNil(cachedList)) {
        return cachedList;
    }
    const iconsList = icons.map((item) => ({
        value: item.Name,
        name: <span>{item.Name}</span>,
        icon: <CustomerJourneyVisualizationIcon svg={item.Svg} />,
    }));
    iconsListCache.set(icons, iconsList);
    return iconsList;
};

const CustomerJourneyVisualizationIconPicker: FC<Props> = (props) => {
    const { icons, value = null, onChange = noop, isRequired = true } = props;

    const { t } = useTranslation();

    const [isVisiblePopover, setIsVisiblePopover] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const iconsList = useMemo<IconListItem[]>(() => getIconsList(icons), [icons]);

    const searchedIconsList = useMemo(
        () => iconsList.filter((item) => item.value.toLowerCase().includes(searchValue.toLowerCase())),
        [iconsList, searchValue],
    );

    const selectedIconData = useMemo(() => iconsList.find((item) => item.value === value) ?? null, [iconsList, value]);

    const validation = useMemo(() => {
        if (!isCJVisualizationIconAllowed(icons, value)) {
            return { isValid: false, errorText: t(l.IsNotValid, { fieldName: t(l.Icon) }) };
        }
        if (!isRequired) {
            return { isValid: true, errorText: null };
        }
        let errorText = null;
        const isValid = yupSchemaValidate(CJVisualizationStepIconValidationSchema(), { icon: value }, (e) => {
            errorText = t(e?.message?.label, { fieldName: t(e?.message?.fieldName) });
        });
        return { isValid, errorText };
    }, [value, icons, t, isRequired]);

    const popoverToggler = () => {
        setIsVisiblePopover((prev) => !prev);
    };

    const searchIconHandler = ({ target }) => {
        setSearchValue(target.value);
    };

    const selectIconHandler = (data) => {
        onChange(data?.value ?? null);
        setIsVisiblePopover(false);
    };

    const iconClearClickHandler = () => {
        selectIconHandler(null);
    };

    const renderSelectedIconLabel = () => {
        if (isNil(selectedIconData)) {
            return null;
        }
        return (
            <div className="option-icon-value">
                {selectedIconData.icon}
                {selectedIconData.name}
            </div>
        );
    };

    return (
        <div className="crm-cj-visualization-step-dropdown">
            <OptionList
                optionLabel={renderSelectedIconLabel()}
                placeholder={t(l.Icon)}
                labelAppearance={LabelAppearanceEnum.Swap}
                toggleHandler={popoverToggler}
                isVisible={isVisiblePopover}
                setIsVisible={setIsVisiblePopover}
                keys={['icon', 'name']}
                titles={[t(l.Icon), t(l.Name)]}
                selectedPropertyKey="value"
                list={searchedIconsList}
                rowCount={searchedIconsList.length}
                searchHandler={searchIconHandler}
                optionValue={value}
                optionSelectHandler={selectIconHandler}
                contentTop={45}
                contentWrapperClassName={classNames('cj-option-list-content-wrapper', styles.iconList)}
                clearable={true}
                onClear={iconClearClickHandler}
                searchValue={searchValue}
                isLoading={false}
                isLoadingList={false}
                isValid={validation.isValid}
                errorText={validation.errorText}
            />
        </div>
    );
};

CustomerJourneyVisualizationIconPicker.displayName = 'CustomerJourneyVisualizationIconPicker';

export default memo(CustomerJourneyVisualizationIconPicker);
